import { GetStaticPropsResult } from 'next'
import dynamic from 'next/dynamic'

import { blogPageModuleQuery } from '@data/sanity/queries/page'
import { SanityBlogPage } from '@data/sanity/queries/types/blog'
import { SanitySiteFragment } from '@data/sanity/queries/types/site'
import { getLocaleLanguage, Locale } from '@lib/language'
import { useBlogCanonicalUrl } from '@lib/metadata'
import {
  BasePageProps,
  getPagePropsFromContext,
  getPageSanityClient,
} from '@lib/page'
import { CustomStaticPropsContext, PageType } from '@lib/routes'
import { getBlogLocale, getBlogPage } from '@lib/sanity/blog'

import Layout from '@components/layout'
import Modules from '@modules/modules'
import { defaultModuleQueryParameters } from '@lib/sanity/page'
const PreviewModules = dynamic(() => import('@modules/preview-modules'))

type BlogPageProps = BasePageProps & {
  blogLocale: Locale
  page: SanityBlogPage
  site: SanitySiteFragment
}

const BlogPage = ({
  draftMode,
  locale,
  blogLocale,
  page,
  site,
}: BlogPageProps) => {
  const canonicalUrl = useBlogCanonicalUrl(
    PageType.BLOG_PAGE,
    '',
    site.blogSettings,
  )

  return (
    <Layout
      page={page}
      site={site}
      canonicalUrl={canonicalUrl}
      draftMode={draftMode}
      className="bg-gray text-black"
    >
      {!draftMode && <Modules posts={page.posts} />}
      {draftMode && (
        <PreviewModules
          posts={page.posts}
          query={blogPageModuleQuery}
          params={{
            ...defaultModuleQueryParameters,
            locale,
            blogLocale,
          }}
        />
      )}
    </Layout>
  )
}

export async function getStaticProps(
  context: CustomStaticPropsContext,
): Promise<GetStaticPropsResult<BlogPageProps>> {
  const locale = getLocaleLanguage(context.locale)

  // Sanity data
  const sanityClient = getPageSanityClient(!!context.draftMode)
  const blogLocale = await getBlogLocale(sanityClient, locale)
  const blogPage = await getBlogPage(sanityClient, locale, blogLocale)

  if (!blogPage.page) {
    return {
      notFound: true,
      revalidate: 60,
    }
  }

  return {
    props: {
      ...getPagePropsFromContext(context),
      blogLocale: blogLocale as Locale,
      page: blogPage.page,
      site: blogPage.site,
    },
    revalidate: 60,
  }
}

export default BlogPage
